// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { TableView } from '@evolvemep/api-ts';
import { alpha, Box, Button, Flex, Loader, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { GridMenu } from 'components/Mantine/GridMenu';

export type Props = {
  checked: boolean;
  loading: boolean;
  isDefault: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  onSave: () => void;
  onChangeDefaultTableView: (tableView: TableView | null) => Promise<void>;
  tableView: TableView;
  canSaveViews: boolean;
};

export const TableViewOption = ({
  checked,
  loading,
  isDefault,
  onClick,
  onDelete,
  onRename,
  onSave,
  onChangeDefaultTableView,
  tableView,
  canSaveViews,
}: Props) => (
  <Button
    component="div"
    variant={checked ? 'primary' : 'transparent'}
    color="dark"
    onClick={onClick}
    pr={0}
    pl={8}
    size="sm"
    ta="start"
    mih="fit-content"
    style={{ cursor: 'pointer', flexShrink: 0 }}
    justify="space-between"
    rightSection={
      loading ? (
        <Box pr="xs">
          <Loader size="sm" color={checked ? 'white' : undefined} />
        </Box>
      ) : (
        <GridMenu
          color={checked ? alpha('var(--mantine-color-white)', 0.8) : 'gray'}
          options={[
            { label: 'Overwrite with current view', value: 'Save', hidden: !canSaveViews },
            { label: isDefault ? 'Remove as default view' : 'Set as default view', value: 'ToggleDefault' },
            { label: 'Rename', value: 'Rename', hidden: !canSaveViews },
            { label: 'Delete', value: 'Delete', hidden: !canSaveViews },
          ]}
          onMenuItemSelected={async (selectedMenuItem) => {
            if (selectedMenuItem === 'Save') {
              onSave();
            }
            if (selectedMenuItem === 'ToggleDefault') {
              const newDefaultTableView = isDefault ? null : tableView;
              await onChangeDefaultTableView(newDefaultTableView);
            }
            if (selectedMenuItem === 'Rename') {
              onRename();
            }
            if (selectedMenuItem === 'Delete') {
              onDelete();
            }
          }}
        />
      )
    }
  >
    <Flex gap="xs" align="center" fw={500} py={4} style={{ whiteSpace: 'normal' }}>
      {checked && <EvolveIcon icon="Check" size="sm" />}
      <Flex direction="column" gap={2}>
        <Text fw="inherit">{tableView.name}</Text>
        {isDefault && (
          <Text
            size="xs"
            c={checked ? alpha('var(--mantine-color-white)', 0.8) : 'gray.6'}
            fw="inherit"
            style={{ whiteSpace: 'nowrap' }}
          >
            (Default)
          </Text>
        )}
      </Flex>
    </Flex>
  </Button>
);
